import { type Channel, Socket } from "phoenix";

import { ACD_CURRENT_ENDPOINT_STATUS, ACD_CURRENT_STATE_QUERY, GET_ACD_TOKEN, GET_ENDPOINT_TOKEN } from "@api/acd";

import acdEndpointApollo from "@/acdEndpointApollo";
import apolloClient from "@/apollo";
import { ACD_SOCKET_PATH } from "@/graphqlUtils";
import handleError from "@/handleError";
import { type AppThunk } from "@/store";

import {
  addCallEvent,
  callDisappears,
  setAcdTokenAndUri,
  setEndpointSocketConnected,
  setEndpointStates,
  setEndpointStatus,
  setEndpointTokenAndUri,
  setUserSocketConnected,
} from "../store/acdReducer";

export let endpointSocket: Nullable<Socket> = null;
export let userSocket: Nullable<Socket> = null;

export const getAcdToken =
  (customer: CustomerRef, project: ProjectRef): AppThunk =>
  async (dispatch) => {
    try {
      const { data } = await apolloClient.mutate<GetAcdTokenType>({
        mutation: GET_ACD_TOKEN,
        variables: {
          customerId: customer.id,
          projectId: project.id,
        },
      });
      if (!data) {
        throw new Error("Cannot fetch ACD token from backend.");
      }

      dispatch(setAcdTokenAndUri(data.acdProjectToken));

      if (userSocket) {
        userSocket.disconnect();
        window.dispatchEvent(new CustomEvent("acd:mainSocketClose"));
      }

      userSocket = new Socket(ACD_SOCKET_PATH, { params: { token: data.acdProjectToken.token } });

      userSocket.onOpen(() => {
        window.dispatchEvent(new CustomEvent("acd:mainSocketOpen"));
        dispatch(setUserSocketConnected(true));
      });
      userSocket.onClose(() => {
        window.dispatchEvent(new CustomEvent("acd:mainSocketClose"));
        dispatch(setUserSocketConnected(false));
      });

      userSocket.connect();
    } catch (e) {
      handleError(e);
      return;
    }
  };

export const resetAcdToken = (): AppThunk => async (dispatch) => {
  if (userSocket) userSocket.disconnect();
  userSocket = null;
  dispatch(setAcdTokenAndUri({ token: null, url: null }));
};

export const getEndpointToken = (): AppThunk => async (dispatch) => {
  try {
    const { data } = await apolloClient.mutate<GetEndpointTokenType>({ mutation: GET_ENDPOINT_TOKEN });
    if (!data) {
      throw new Error("Cannot get endpoint token from backend.");
    }

    dispatch(setEndpointTokenAndUri(data.acdEndpointToken));

    if (endpointSocket) {
      endpointSocket.disconnect();
      window.dispatchEvent(new CustomEvent("acd:socketClose"));
    }

    endpointSocket = new Socket(ACD_SOCKET_PATH, { params: { token: data.acdEndpointToken.token } });

    endpointSocket.onOpen(() => {
      window.dispatchEvent(new CustomEvent("acd:socketOpen"));
      dispatch(handleEndpointSocketConnected(endpointSocket!));
    });
    endpointSocket.onClose(() => {
      window.dispatchEvent(new CustomEvent("acd:socketClose"));
      dispatch(handleEndpointSocketConnected(endpointSocket!));
    });

    endpointSocket.connect();
  } catch (e) {
    handleError(e);
    return;
  }
};

export const dispatchIncomingCall = (value: AcdCallEventType) => {
  const event = new CustomEvent("tt:incomingCall", { detail: value });
  document.dispatchEvent(event);

  return addCallEvent(value);
};

export const dispatchCallEvent = (value: AcdCallEventType) => {
  const event = new CustomEvent("tt:callEvent", { detail: value });
  document.dispatchEvent(event);

  if (value.event.msg.match(/setup/)) {
    const event = new CustomEvent("tt:callStart", { detail: value });
    document.dispatchEvent(event);
  }

  return addCallEvent(value);
};

export const callDoesDisappear =
  (callEvent: AcdCallEventType): AppThunk =>
  async (dispatch, getState) => {
    const event = new CustomEvent("tt:callEnd", { detail: callEvent });
    document.dispatchEvent(event);

    const { currentProject, currentUser } = getState().session;
    if (!currentProject || !currentUser) return;

    dispatch(callDisappears(callEvent));
  };

let acdChannel: Nullable<Channel> = null;
let acdUserChannel: Nullable<Channel> = null;

export const handleEndpointSocketConnected =
  (socket: Socket): AppThunk =>
  async (dispatch, getState) => {
    const connState = socket.isConnected();
    const { currentUser } = getState().session;
    dispatch(setEndpointSocketConnected(connState));

    if (connState && !acdChannel) {
      const { data } = await acdEndpointApollo.query<AcdCurrentEndpointStateQueryType>({
        query: ACD_CURRENT_STATE_QUERY,
      });

      if (data?.currentEndpointState) {
        dispatch(setEndpointStates(data.currentEndpointState));
      }

      acdChannel = socket.channel("endpoint:lobby");
      acdChannel
        .join()
        .receive("ok", () => {})
        .receive("error", (msg) => console.log("error joining endpoint lobby", msg));

      acdChannel.on("endpoint_status_changed", (msg: AcdEndpointStatus) => {
        // I don't get it. Sometimes either ep.lastEvent or msg.lastEvent seems to be null 🤯
        const state = getState().acd.endpointStates.filter((ep) => ep.lastEvent?.e164 !== msg.lastEvent?.e164);
        if (msg.type !== "USER_DISAPPEARS") {
          state.push(msg);
        }
        dispatch(setEndpointStates(state));
      });
    }

    if (currentUser?.attrs?.e164 && connState && !acdUserChannel) {
      try {
        const { data } = await acdEndpointApollo.query<AcdCurrentEndpointStatusDataType>({
          query: ACD_CURRENT_ENDPOINT_STATUS,
          variables: { no: currentUser.attrs.e164 },
        });

        if (data?.currentEndpointStatus) {
          dispatch(setEndpointStatus(data.currentEndpointStatus));
        }
      } catch (e) {
        handleError(e, false);
      }

      acdUserChannel = socket.channel(`endpoint:${currentUser.attrs.e164}`);
      acdUserChannel
        .join()
        .receive("ok", () => {})
        .receive("error", (msg) => console.log("could not join acd user channel", msg));

      acdUserChannel.on("endpoint_status_changed", (msg: AcdEndpointStatus) => {
        if (msg.type === "USER_DISAPPEARS") {
          dispatch(setEndpointStatus(null));
        } else {
          dispatch(setEndpointStatus(msg));
        }
      });

      acdUserChannel.on("call_event", (msg: AcdCallEventType) => {
        if (msg.direction === "INBOUND" && msg.eventType === "NEW") {
          dispatch(dispatchIncomingCall(msg));
        }
        dispatch(dispatchCallEvent(msg));
        if (msg.eventType === "DISAPPEAR") {
          dispatch(callDoesDisappear(msg));
        }
      });
    }
  };
